import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import AlbumSelectionPage from "./components/AlbumSelectionPage";
import AlbumPageDesigner from "./components/AlbumPageDesigner";
import LoginPage from "./components/LoginPage";
import UserProjects from "./components/UserProjects";
import SignUpPage from "./components/SignUpPage";
import ForgotPasswordPage from "./components/ForgotPasswordPage";
import ResetPasswordPage from "./components/ResetPasswordPage"
import PublicProofPages from "./pages/PublicProofPages";
import { useAuth } from "./contexts/AuthContext";
import WebFont from "webfontloader";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme"; //  this is your custom theme
import { BrowserRouter } from "react-router-dom";


// ----- AlbumEditorWrapper -----
function AlbumEditorWrapper({ token }: { token: string }) {
    const { id } = useParams<{ id: string }>();
    const [albumData, setAlbumData] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (!id) return;

        fetch(`${API_URL}/albums/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => {
                if (!res.ok) throw new Error("Failed to fetch album");
                return res.json();
            })
            .then((data) => {
               // console.log("Fetched album data:", data);

                // Normalize data shape
                const normalizedData = {
                    ...data,
                    size: data.albumSize ?? { width: 0, height: 0, unit: "cm" },
                    pageCount: data.pageCount ?? 0,
                    backgroundColor: data.backgroundColor ?? "#ffffff",
                    albumName: data.albumName ?? data.name ?? "",
                    cutArea: data.cutArea ?? 0,
                    data: data.data ?? {},
                    printExported: data.printExported ?? false,      // <-- add this
                    printExportedAt: data.printExportedAt ?? null,  // <-- add this
                };

                setAlbumData(normalizedData);
                setLoading(false);
            })
            .catch((err) => {
                alert(err.message);
                setLoading(false);
            });
    }, [id, token]);

    if (loading) return <div>Loading album...</div>;
    if (!albumData) return <div>Album not found.</div>;

    return (
        <AlbumPageDesigner
            albumType={albumData.albumType || ""}
            size={albumData.size}
            loadedProjectData={albumData}
            pageCount={albumData.pageCount}
            backgroundColor={albumData.backgroundColor}
            albumName={albumData.albumName}
            cutArea={albumData.cutArea}
            unit={albumData.size.unit}
            spineWidth={albumData.spineWidth ?? 0}
        />
    );
}

// ----- App -----
function App() {
    const { token, loading } = useAuth();
    const navigate = useNavigate();

    const [albumType, setAlbumType] = useState("");
    const [albumSize, setAlbumSize] = useState<{ width: number; height: number; unit: "cm" | "inches" } | null>(null);
    const [, setAlbumCreated] = useState(false);
    const [pageCount, setPageCount] = useState<number>(0);
    const [globalBackgroundColor, setGlobalBackgroundColor] = useState("#ffffff");
    const [loadedProjectData, setLoadedProjectData] = useState<any>(null);
    const [albumName, setAlbumName] = useState("");
    const [cutArea, setCutArea] = useState<number>(0);
    const [spineWidth, setSpineWidth] = useState<number>(0);

    //  Load cached Google Fonts on app start
    useEffect(() => {
        try {
            const cachedFonts = JSON.parse(localStorage.getItem("loadedGoogleFonts_v1") || "{}");
            const cachedFontNames = Object.keys(cachedFonts);

            if (cachedFontNames.length > 0) {
                WebFont.load({
                    google: { families: cachedFontNames },
                    active: () => console.log(" Cached Google Fonts restored globally"),
                } as any); //  fix TypeScript error
            } else {
               // console.log(" No cached fonts found");
            }
        } catch (err) {
            console.warn(" Failed to restore cached fonts:", err);
        }
    }, []);


    const handleCreateAlbum = (
        type: string,
        size: { width: number; height: number; unit: "cm" | "inches" },
        selectedCount: number,
        backgroundColor: string,
        name: string,
        cutArea: number,
        unit: "cm" | "inches",
        spineWidth?: number
    ) => {
        setAlbumType(type);
        setAlbumSize(size);
        setPageCount(selectedCount);
        setGlobalBackgroundColor(backgroundColor);
        setAlbumName(name);
        setCutArea(cutArea);
        setSpineWidth(spineWidth ?? 0); //  this stores your computed spine width
        setLoadedProjectData(null);
        setAlbumCreated(true);
        navigate("/upload");
    }

    const handleOpenProject = (album: any) => {
        navigate(`/album/${album._id}`);
    };

    /* -------------------------------------------------------
       PUBLIC ROUTES (NO AUTH)
   ------------------------------------------------------- */

    if (loading) {
        return <div>Loading...</div>;
    }


    return (
        <ThemeProvider theme={theme}>
            <Routes>
                {/*  PUBLIC PROOF PAGE */}
                <Route
                    path="/eda_online/proof/:username/:exportId/*"
                    element={<PublicProofPages />}
                />

                {/* -------------------------------------------------
                   AUTHENTICATION ROUTES
                ------------------------------------------------- */}

                {!token && (
                    <>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/signup" element={<SignUpPage />} />
                        <Route path="*" element={<Navigate to="/login" replace />} />
                    </>
                )}

                {/* -------------------------------------------------
                   PROTECTED APP ROUTES
                ------------------------------------------------- */}

                {token && (
                    <>
                        {/*  Redirect root to album selection */}
                        <Route path="/" element={<Navigate to="/album-selection" replace />} />
                        {/*  Main selection page */}
                        <Route
                            path="/album-selection"
                            element={
                                <AlbumSelectionPage
                                    onSelect={handleCreateAlbum}
                                    onOpenProject={handleOpenProject}
                                />
                            }
                        />

                        {/*  Keep this redirect to handle album creation flow */}
                        <Route path="/upload" element={<Navigate to="/page-layout" replace />} />


                        {/*  Designer page */}
                        <Route
                            path="/page-layout"
                            element={
                                <AlbumPageDesigner
                                    albumType={albumType}
                                    size={albumSize ?? { width: 0, height: 0, unit: "cm", }}
                                    loadedProjectData={loadedProjectData}
                                    pageCount={pageCount}
                                    backgroundColor={globalBackgroundColor}
                                    albumName={albumName}
                                    cutArea={cutArea}
                                    unit={albumSize?.unit ?? "cm"}
                                    spineWidth={spineWidth}
                                />
                            }
                        />

                        {/*  Editing existing albums */}
                        <Route
                            path="/album/:id" element={<AlbumEditorWrapper token={token} />} />

                        <Route path="/projects" element={<UserProjects />} />
                        <Route path="*" element={<Navigate to="/album-selection" replace />} />
                    </>
                )}
            </Routes>
        </ThemeProvider>
    );
}

export default App;
